<template>
  <div class="message-composer">
    <!-- Always mounted: a live region inserted alongside its content isn't
    reliably announced. Failure rows themselves stay out of it so their
    Retry/Dismiss buttons aren't read along with the copy. -->
    <div class="sr-only" aria-live="polite">{{ failureAnnouncement }}</div>
    <div v-if="failureRows.length > 0" class="message-composer__failures">
      <div
        v-for="row in failureRows"
        :key="row.id"
        class="message-composer__failure-row"
        :class="
          row.retryable
            ? 'message-composer__failure-row--retryable'
            : 'message-composer__failure-row--terminal'
        "
      >
        <v-icon
          size="16"
          :color="row.retryable ? 'warning' : 'error'"
          class="mr-2"
          >{{
            row.retryable
              ? "fa-solid fa-triangle-exclamation"
              : "fa-solid fa-circle-xmark"
          }}</v-icon
        >
        <span class="message-composer__failure-text">{{ row.copy }}</span>
        <div class="message-composer__failure-actions">
          <button
            v-if="row.retryable"
            type="button"
            class="message-composer__failure-link"
            :aria-label="
              row.filename ? `Retry uploading ${row.filename}` : 'Retry upload'
            "
            @click="retryAttachment(row.id)"
          >
            Retry
          </button>
          <button
            type="button"
            class="message-composer__failure-link"
            data-focus-group="failure-remove"
            :aria-label="
              row.everQueued
                ? row.filename
                  ? `Remove ${row.filename}`
                  : 'Remove'
                : row.filename
                ? `Dismiss ${row.filename}`
                : 'Dismiss'
            "
            @click="removeAttachment(row.id, $event)"
          >
            {{ row.everQueued ? "Remove" : "Dismiss" }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="chipAttachments.length > 0"
      class="message-composer__attachments"
    >
      <div
        v-for="attachment in chipAttachments"
        :key="attachment.id"
        class="message-composer__chip"
      >
        <div class="message-composer__chip-row">
          <v-icon
            size="14"
            class="mr-2"
            :class="{
              'message-composer__chip-icon--queued':
                attachment.status === 'queued'
            }"
            >fa-regular fa-file</v-icon
          >
          <span
            class="message-composer__chip-filename"
            :class="{
              'message-composer__chip-filename--queued':
                attachment.status === 'queued'
            }"
            >{{ attachment.filename }}</span
          >
          <button
            type="button"
            class="message-composer__chip-remove"
            data-focus-group="chip-remove"
            :disabled="['uploading', 'processing'].includes(attachment.status)"
            :aria-label="`Remove ${attachment.filename}`"
            @click="removeAttachment(attachment.id, $event)"
          >
            <v-icon size="14">fa-solid fa-xmark</v-icon>
          </button>
        </div>
        <v-progress-linear
          v-if="attachment.status === 'uploading'"
          :model-value="cappedUploadProgress(attachment)"
          height="2"
          color="primary"
          class="message-composer__chip-progress"
        ></v-progress-linear>
        <v-progress-linear
          v-else-if="attachment.status === 'processing'"
          indeterminate
          height="2"
          color="primary"
          class="message-composer__chip-progress"
        ></v-progress-linear>
      </div>
    </div>

    <div class="d-flex align-end ga-2">
      <input
        ref="fileInput"
        type="file"
        multiple
        :accept="ACCEPT_ATTR"
        class="d-none"
        @change="onFilesSelected"
      />
      <v-btn
        ref="attachButton"
        icon
        variant="outlined"
        class="message-composer__attach"
        title="Attach a file"
        @click="triggerFilePicker"
      >
        <v-icon size="18">fa-solid fa-paperclip</v-icon>
      </v-btn>

      <v-textarea
        v-model="body"
        placeholder="Write a message..."
        density="comfortable"
        variant="solo-filled"
        flat
        rows="1"
        auto-grow
        hide-details
        class="message-composer__input"
        :error="isOverLimit"
        @update:model-value="onInput"
        @keydown.enter.exact.prevent="send"
      ></v-textarea>

      <v-btn
        icon
        color="primary"
        :disabled="!canSend"
        :loading="sending"
        aria-describedby="message-composer-send-reason"
        @click="send"
      >
        <v-icon>fa-solid fa-paper-plane</v-icon>
      </v-btn>
    </div>
    <div
      id="message-composer-send-reason"
      class="message-composer__send-reason text-caption text-medium-emphasis"
    >
      {{ sendDisabledReason }}
    </div>
    <div
      class="message-composer__counter text-caption"
      :class="isOverLimit ? 'text-error' : 'text-medium-emphasis'"
    >
      {{ counterText }}
    </div>

    <ContextualBanner class="message-composer__banner" />
  </div>
</template>

<script>
import { debounce } from "lodash";
import messagingApi from "@/services/messagingApi";
import ContextualBanner from "@/components/messaging/ContextualBanner.vue";
import {
  ACCEPT_ATTR,
  classifyFailure,
  classifyBatch,
  classifyBatchFailure,
  classifyFile,
  sendFailureCopy
} from "@/services/messagingLimits";

const BODY_MAX_CHARS = 1000;
// Counter stays hidden below this - only shows once someone's actually
// approaching the limit, then counts down, then flips to an over-limit
// count once they cross it. No hard stop on typing past BODY_MAX_CHARS;
// canSend is what actually blocks the send, not the textarea itself.
const COUNTER_THRESHOLD = 800;

export default {
  name: "MessageComposer",
  components: { ContextualBanner },
  props: {
    conversationToken: { type: String, required: true },
    draft: { type: Object, default: null }
  },
  emits: ["sent", "typing"],
  data() {
    return {
      body: this.draft?.body || "",
      sending: false,
      BODY_MAX_CHARS,
      ACCEPT_ATTR,
      composeToken: this.draft?.composeToken || crypto.randomUUID(),
      failureAnnouncement: "",
      attachments: (this.draft?.attachments || []).map(a => ({
        ...a,
        status: "uploaded"
      }))
    };
  },
  computed: {
    // Chips show only files in the message or on their way into it - failed
    // files get their own row above, never a chip.
    chipAttachments() {
      return this.attachments.filter(a => a.status !== "failed");
    },
    failureRows() {
      return this.attachments
        .filter(a => a.status === "failed")
        .map(a => ({
          id: a.id,
          filename: a.filename,
          retryable: a.retryable,
          copy: a.copy,
          everQueued: !!a.everQueued
        }));
    },
    inFlight() {
      return this.attachments.some(a =>
        ["queued", "uploading", "processing"].includes(a.status)
      );
    },
    uploadedAttachmentIds() {
      return this.attachments
        .filter(a => a.status === "uploaded")
        .map(a => a.id);
    },
    isOverLimit() {
      return this.body.length > BODY_MAX_CHARS;
    },
    // null below the threshold - the template still renders the wrapping
    // div either way, so the slot stays reserved and nothing shifts when
    // this appears.
    counterText() {
      const remaining = BODY_MAX_CHARS - this.body.length;
      if (this.body.length < COUNTER_THRESHOLD) return "";
      if (remaining >= 0) return `${remaining} characters left`;
      return `${Math.abs(remaining)} characters over the limit`;
    },
    blockingFailures() {
      return this.failureRows.filter(row => row.everQueued);
    },
    sendDisabledReason() {
      if (this.inFlight) {
        const count = this.attachments.filter(a =>
          ["queued", "uploading", "processing"].includes(a.status)
        ).length;
        return count === 1
          ? "Waiting for 1 file to finish uploading."
          : `Waiting for ${count} files to finish uploading.`;
      }
      if (this.blockingFailures.length > 0) {
        const count = this.blockingFailures.length;
        return count === 1
          ? "1 file needs attention before this can send."
          : `${count} files need attention before this can send.`;
      }
      return "";
    },
    canSend() {
      return (
        (this.body.trim().length > 0 ||
          this.uploadedAttachmentIds.length > 0) &&
        !this.isOverLimit &&
        !this.sending &&
        !this.inFlight &&
        this.blockingFailures.length === 0
      );
    }
  },
  created() {
    this.debouncedSaveDraft = debounce(this.saveDraft, 3000);
  },
  methods: {
    onInput() {
      this.$emit("typing");
      this.debouncedSaveDraft();
    },
    saveDraft() {
      messagingApi
        .saveDraft({
          conversationToken: this.conversationToken,
          composeToken: this.composeToken,
          body: this.body,
          attachmentIds: this.uploadedAttachmentIds
        })
        .catch(() => {}); // best-effort - a failed autosave shouldn't interrupt composing
    },
    triggerFilePicker() {
      this.$refs.fileInput.click();
    },
    // Every selected file becomes a row immediately, in selection order -
    // queued if it passes client-side checks, failed (never a chip) if it
    // doesn't. The queued ones then upload one at a time; a file rejected
    // at selection never touches the network at all.
    async onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = ""; // allow re-selecting the same file later
      if (files.length === 0) return;
      const existingCount = this.chipAttachments.length;
      const existingBytes = this.chipAttachments.reduce(
        (sum, a) => sum + (a.fileSize || 0),
        0
      );
      const batchCode = classifyBatch(existingCount, existingBytes, files);
      if (batchCode) {
        const { retryable, copy } = classifyBatchFailure(batchCode);
        this.attachments.push({
          id: `local-${Date.now()}-${Math.random()}`,
          filename: null,
          status: "failed",
          retryable,
          copy: copy(),
          file: null,
          everQueued: false
        });
        this.announceFailures([copy()]);
        return;
      }

      // Batch passed - now check each file individually for type/size,
      // properties of that one specific file.
      const queued = [];
      const failureCopies = [];
      files.forEach(file => {
        const code = classifyFile(file);
        const localId = `local-${Date.now()}-${Math.random()}`;

        if (code) {
          const { retryable, copy } = classifyFailure(code, false);
          this.attachments.push({
            id: localId,
            filename: file.name,
            fileSize: file.size,
            fileType: file.type,
            status: "failed",
            retryable,
            copy: copy(file.name),
            file: retryable ? file : null,
            everQueued: false
          });
          failureCopies.push(copy(file.name));
          return;
        }

        const row = {
          id: localId,
          filename: file.name,
          fileSize: file.size,
          fileType: file.type,
          status: "queued",
          file
        };
        this.attachments.push(row);
        queued.push(row);
      });
      if (failureCopies.length > 0) this.announceFailures(failureCopies);

      for (const row of queued) {
        // eslint-disable-next-line no-await-in-loop
        await this.attachFile(row);
      }
    },
    async attachFile(row) {
      this.replaceAttachment(row.id, {
        ...row,
        status: "uploading",
        uploadProgress: 0
      });

      try {
        const { data } = await messagingApi.createAttachment({
          composeToken: this.composeToken,
          file: row.file,
          onUploadProgress: event => this.onUploadProgress(row.id, event)
        });
        this.replaceAttachment(row.id, { ...data, status: "uploaded" });
        this.saveDraft();
      } catch (err) {
        const code = err.response?.data?.error?.code;
        const { retryable, copy } = classifyFailure(code, true);
        this.replaceAttachment(row.id, {
          ...row,
          status: "failed",
          retryable,
          copy: copy(row.filename),
          file: retryable ? row.file : null,
          everQueued: true
        });
        this.announceFailures([copy(row.filename)]);
      }
    },
    // Fires repeatedly while bytes are still going out. Reads the row fresh
    // from state each time (not the closure's `row`) since it can fire many
    // times over the life of one attachFile call. Once every byte is sent,
    // there's nothing left to report until the response comes back -
    // CarrierWave's validation/HEIC conversion/S3 store all happen in that
    // gap - so that's the moment to flip from "uploading" to "processing".
    onUploadProgress(id, event) {
      const current = this.attachments.find(a => a.id === id);
      if (!current || current.status !== "uploading") return;

      const total = event.total || event.loaded;
      const progress = total ? Math.round((event.loaded / total) * 100) : 0;

      if (progress >= 100) {
        this.replaceAttachment(id, { ...current, status: "processing" });
      } else {
        this.replaceAttachment(id, { ...current, uploadProgress: progress });
      }
    },
    cappedUploadProgress(attachment) {
      return Math.min(attachment.uploadProgress ?? 0, 92);
    },
    retryAttachment(id) {
      const row = this.attachments.find(a => a.id === id);
      if (!row || !row.retryable || !row.file) return;

      this.attachFile(row);
    },
    replaceAttachment(localId, replacement) {
      const index = this.attachments.findIndex(a => a.id === localId);
      if (index !== -1) this.attachments.splice(index, 1, replacement);
    },
    // Cleared first so a repeat of the same copy (e.g. a retry that fails
    // the same way) is still a change the screen reader will announce.
    async announceFailures(copies) {
      this.failureAnnouncement = "";
      await this.$nextTick();
      this.failureAnnouncement = copies.join(" ");
    },
    // The clicked button unmounts with its row, which would drop focus to
    // <body>. Land on the button now in the same slot of the same list
    // (or the last one, if the removed row was last), else the paperclip.
    async removeAttachment(id, event) {
      const button = event?.currentTarget;
      const group = button?.dataset.focusGroup;
      const groupButtons = () =>
        group
          ? [...this.$el.querySelectorAll(`[data-focus-group="${group}"]`)]
          : [];
      const index = groupButtons().indexOf(button);

      this.attachments = this.attachments.filter(a => a.id !== id);
      this.saveDraft();

      if (!button) return;
      await this.$nextTick();
      const remaining = groupButtons().filter(b => !b.disabled);
      const target =
        remaining[Math.min(index, remaining.length - 1)] ||
        this.$refs.attachButton?.$el;
      target?.focus();
    },
    async send() {
      if (!this.canSend) return;

      this.sending = true;
      try {
        const { data } = await messagingApi.sendMessage({
          conversationToken: this.conversationToken,
          body: this.body,
          attachmentIds: this.uploadedAttachmentIds,
          idempotencyKey: crypto.randomUUID()
        });
        this.body = "";
        this.attachments = [];
        this.composeToken = crypto.randomUUID();
        this.$emit("sent", data.message);
      } catch (err) {
        const code = err.response?.data?.error?.code;
        // sendFailureCopy only covers the attachment-set-changed-under-us
        // codes; every other send rejection still falls back to a generic
        // line rather than the raw code string.
        window.Bus?.$emit(
          "error-message",
          sendFailureCopy(code) || "Something went wrong sending that message."
        );
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@use "../../styles/base/variables" as *;

.message-composer {
  padding: 12px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.message-composer__attach {
  border-color: $uwill-peri-50 !important;
  color: $uwill-peri !important;
}

.message-composer__failures {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.message-composer__failure-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;

  &--retryable {
    background-color: rgba($uwill-warning, 0.12);
  }

  &--terminal {
    background-color: rgba($uwill-error, 0.1);
  }
}

.message-composer__failure-text {
  flex: 1;
  color: $uwill-black;
}

.message-composer__failure-actions {
  display: flex;
  gap: 12px;
  margin-left: 12px;
}

// Real buttons (not a pipe-separated link) so Retry/Remove each get their
// own accessible name and hit area, styled to read as text links per the
// mock rather than chip/button chrome. min-width/height keeps the hit
// target at 24x24 (SC 2.5.8) even though the visible text is smaller.
.message-composer__failure-link {
  min-width: 24px;
  min-height: 24px;
  padding: 0;
  border: none;
  background: none;
  color: $link-color;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: $link-color-hover;
  }
}
.message-composer__attachments {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.message-composer__chip {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #fff;
}

.message-composer__chip-row {
  display: flex;
  align-items: center;
}

.message-composer__chip-filename {
  flex: 1;
  color: $uwill-black;

  &--queued {
    color: $uwill-neutral;
  }
}

.message-composer__chip-icon--queued {
  color: $uwill-neutral !important;
}

// Real button (not just a chip's built-in close glyph) so it keeps its own
// accessible name and a 24x24 hit target regardless of the icon's size.
.message-composer__chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  margin-left: 8px;
  padding: 0;
  border: none;
  background: none;
  color: $uwill-neutral;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
}

.message-composer__chip-progress {
  margin-top: 4px;
  border-radius: 2px;
}

.message-composer__input {
  flex: 1;

  :deep(.v-field),
  :deep(.v-field__overlay) {
    background-color: $uwill-neutral-500 !important;
    opacity: 1 !important;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: none !important;
    border: none !important;
  }
}

.message-composer__send-reason {
  text-align: center;
  padding: 4px 0 0;
  min-height: 1.25rem;
}

.message-composer__counter {
  text-align: center;
  padding: 4px 0 8px;
  // Reserved even when empty (below the 800-char threshold) so the counter
  // appearing/disappearing never shifts the composer's layout.
  min-height: 1.25rem;
}

.message-composer__banner {
  margin: 0 -16px; // full-bleed past the composer's own horizontal padding
}
</style>
